import React, { useCallback, useEffect, useRef, useState } from "react";
import { render } from 'react-dom';
import { Transition } from 'react-transition-group';
import { DropdownMenuMobile } from '../components/DropdownMenuMobile';
import { MegaMenuMobile } from '../components/MegaMenuMobile';
import { trapFocus, removeTrapFocus } from '../utils/trapFocus';

(function init() {
  function loadComponent() {
    const rootElement = document.querySelector('[data-mobile-menu]');
    if (rootElement) render(<MobileMenu />, rootElement);
  }

  document.addEventListener("shopify:section:load", loadComponent);
  loadComponent();
})();

function MobileMenu() {
  const menu = JSON.parse(document.querySelector('[data-menu]').innerHTML);
  const menuSettings = JSON.parse(document.querySelector('[data-menu-settings]').innerHTML);

  const content = useRef(null);
  const trigger = useRef(null);

  const [open, setOpen] = useState(null);
  const [activeDropdown, setActiveDropdown] = useState(null);

  const [menuHeight, setMenuHeight] = useState(null);

  const toggleOpen = useCallback(() => {
    setOpen((open) => !open);
    setActiveDropdown(null);
  }, []);

  const handleDropdownClick = useCallback((index) => {
    setActiveDropdown((prev) => prev !== index ? index : null);      
  }, []);

  const closeDropdown = useCallback(() => {
    setActiveDropdown(null);      
  }, []);

  /*
    Transitions
  */
  const transitionRef = useRef(null);
  const defaultStyle = {
    transition: `opacity 300ms ease-in-out`,
    opacity: 0
  }
  const transitionStyles = {
    entering: { opacity: 1, visibility: 'visible' },
    entered:  { opacity: 1, visibility: 'visible' },
    exiting:  { opacity: 0, visibility: 'visible' },
    exited:  { opacity: 0, visibility: 'hidden' },
  };

  /*
    Event listeners
  */

  useEffect(() => {
    if (transitionRef) {
      const rect = transitionRef.current.getBoundingClientRect().top;
      const heightStr = (rect / 16).toString();
      setMenuHeight(heightStr);
    }
  }, [transitionRef, open]);
  
  useEffect(() => {
    if (trigger) {
      // If mobile menu is open and the escape key is pressed, close and return focus to trigger
      document.addEventListener('keyup', (e) => {
        const open = trigger.current.getAttribute('aria-expanded') === 'true';
        if (e.key === 'Escape' && open) {
          trigger.current.focus();
          setOpen(false);
        }
      });

      // If there's a click on the document and 1) mobile menu is open 2) the target is not the trigger or the menu contents, then close mobile menu
      // TODO: Fix width of button as clicking next to it doesn't close it
      document.addEventListener('click', (e) => {
        const open = trigger.current.getAttribute('aria-expanded') === 'true';
        if (open) {
          if (
            !e.target.hasAttribute('data-mobile-menu-trigger') &&
            e.target.closest('[data-mobile-menu-parent]') === null
          ) {
            setOpen(false);
          }
        }
      });
    }
  }, [trigger]);

  useEffect(() => {
    if (open) {
      document.body.classList.add('overflow-hidden', 'fixed', 'w-full', 'overscroll-none', 'touch-none');
    } else {
      document.body.classList.remove('overflow-hidden', 'fixed', 'w-full', 'overscroll-none', 'touch-none');
    }
  }, [open]);

  
  useEffect(() => {
    // When menu is open, trap focus inside it (but don't force focus on first element)
    if (open && activeDropdown === null) {
      trapFocus(content.current, false);
    } else {
      removeTrapFocus();
    }
  }, [open, activeDropdown]);

  if (menu.length === 0) {
    return null;
  }

  return (
    <div 
      className="h-full"
      ref={content}
      data-mobile-menu-parent
    >
      <button 
        className="flex items-center h-full"
        aria-expanded={open}
        aria-controls={`MobileMenu-JS`}
        onClick={toggleOpen}
        ref={trigger}
        data-mobile-menu-trigger
      >
        <svg aria-hidden="true" aria-focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={`pointer-events-none transition-opacity ${open ? 'opacity-0' : 'opacity-1'}`}>
          <path d="M3 4H21V6H3V4ZM3 11H15V13H3V11ZM3 18H21V20H3V18Z" fill="currentColor"/>
        </svg>
        <svg aria-hidden="true" aria-focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={`pointer-events-none absolute left-4 transition-opacity ${open ? 'opacity-1' : 'opacity-0'}`}>
          <path d="M12 10.586L16.95 5.63599L18.364 7.04999L13.414 12L18.364 16.95L16.95 18.364L12 13.414L7.04999 18.364L5.63599 16.95L10.586 12L5.63599 7.04999L7.04999 5.63599L12 10.586Z" fill="currentColor"/>
        </svg>
        <span className="sr-only">Mobile menu</span>
      </button>

      <Transition transitionRef={transitionRef} in={open} timeout={500}>
        {state => (
          <div 
            ref={transitionRef} 
            style={{
              ...defaultStyle,
              ...transitionStyles[state],
              height: `calc(100dvh - ${menuHeight}rem)`
            }}
            id={`MobileMenu-JS`}
            className={`absolute overflow-y-auto h-full left-0 z-10 bg-blush-100 w-full h-screen min-h-[50vh] overflow-x-hidden`}
          >
              <ul className="list-none text-lg font-medium pt-[3rem]" role="list">
                {menu.map((item, index) => {
                  if ('content' in item) {
                    return (
                      <li className="list-none" key={index}>
                        <MegaMenuMobile item={item} index={index} handleDropdownClick={handleDropdownClick} open={index === activeDropdown} closeDropdown={closeDropdown} menuHeight={menuHeight} />
                      </li>
                    )
                  } else {
                    if ('links' in item) {
                      return (
                        <li className="list-none" key={index}>
                          <DropdownMenuMobile item={item} index={index} handleDropdownClick={handleDropdownClick} open={index === activeDropdown} closeDropdown={closeDropdown} menuHeight={menuHeight} />
                        </li>
                      )
                    }
                    return (
                      <li className="list-none" key={index}>
                        <a href={item.url} 
                          className={`block px-4 py-2 underline underline-offset-4 decoration-transparent hover:decoration-black transition uppercase ${item.current && `decoration-black`}`}
                          aria-current={item.current}>
                          <span>{item.title}</span>
                        </a>
                      </li>
                    )
                  }
                })}
              </ul>
              <ul className="list-none text-[0.8125rem] font-medium py-[3rem]">
                <li className="list-none">
                  <a href={menuSettings.account_url} className="block px-4 py-1 underline underline-offset-4 decoration-transparent hover:decoration-black transition">
                    {menuSettings.account_text}
                  </a>
                </li>

                {menuSettings.cta_text && menuSettings.cta_url &&
                  <li className="list-none">
                    <a href={menuSettings.cta_url} className="block px-4 py-1 underline underline-offset-4 decoration-transparent hover:decoration-black transition">
                      {menuSettings.cta_text}
                    </a>
                  </li>
                }
                  
                <li className="list-none">
                  <a href="mailto:support@myparfait.com" className="block px-4 py-1 underline underline-offset-4 decoration-transparent hover:decoration-black transition">
                    Contact us
                  </a>
                </li>
              </ul>
          </div>
        )}
      </Transition>
    </div>
  )
}